"use client"

import { PropsWithChildren, useEffect, useMemo, useState } from "react"
import { responsiveWidthVariants } from "@/constants/tailwindClasses"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { storyblokEditable } from "@storyblok/react/rsc"

import type { CommonPageContext } from "@/lib/tracking/tracking"
import { cn } from "@/lib/utils"
import { UbContextDependentStoryblok } from "@/components/storyblok/component-types"

interface StorefrontContextResponse extends CommonPageContext {
  oci: boolean
}

interface UbContextDependentClientProps extends PropsWithChildren {
  blok: UbContextDependentStoryblok
  showContent: boolean
  matchAll: boolean
  sbLocale?: string
}

export default function UbContextDependentClient({
  blok,
  showContent,
  matchAll,
  sbLocale,
  children,
}: UbContextDependentClientProps) {
  const isB2B: boolean = blok.isB2B || false
  const isB2C: boolean = blok.isB2C || false
  const isOCI: boolean = blok.isOCI || false
  const isNotOCI: boolean = blok.isNotOCI || false
  const isAnonymous: boolean = blok.isAnonymous || false
  const isNotAnonymous: boolean = blok.isNotAnonymous || false

  const basketNotEmpty: boolean = blok.basketNotEmpty || false
  const basketEmpty: boolean = blok.basketEmpty || false
  const negation: boolean = blok.negation || false

  const basketHasInvoiceToAddress: boolean = blok.basketHasInvoiceToAddress || false
  const basketHasShipToAddress: boolean = blok.basketHasShipToAddress || false
  const basketShipToAddressSameAsInvoiceToAddress: boolean = blok.basketShipToAddressSameAsInvoiceToAddress || false
  const hasAddresses: boolean = blok.hasAddresses || false

  const needsStorefrontContext = isB2B || isB2C || isOCI || isNotOCI || isAnonymous || isNotAnonymous
  const [storefrontContext, setStorefrontContext] = useState<StorefrontContextResponse | undefined>()

  const { basketData, basketLineItems, addresses } = useUbStorefrontState()

  useEffect(() => {
    if (!needsStorefrontContext || storefrontContext) {
      return
    }

    const controller = new AbortController()
    const contextUrl = sbLocale
      ? `/api/storefront/context?sbLocale=${encodeURIComponent(sbLocale)}`
      : "/api/storefront/context"

    fetch(contextUrl, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((context: StorefrontContextResponse | undefined) => {
        if (context) {
          setStorefrontContext(context)
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load storefront context for context-dependent content", error)
        }
      })

    return () => controller.abort()
  }, [needsStorefrontContext, sbLocale, storefrontContext])

  const hasLineItems = useMemo(() => {
    const qty = basketData?.data?.totalProductQuantity ?? 0
    const liCount = basketLineItems?.length ?? 0
    // Consider basket non-empty when either quantity > 0 or we have line items loaded
    return qty > 0 || liCount > 0
  }, [basketData?.data?.totalProductQuantity, basketLineItems])

  const hasInvoiceToAddress = useMemo(() => {
    const invoiceToAddressExists = !!basketData?.data?.invoiceToAddress
    // console.log("invoiceToAddressExists", invoiceToAddressExists)
    return invoiceToAddressExists
  }, [basketData?.data?.invoiceToAddress])

  const hasShipToAddress = useMemo(() => {
    const commonShipToAddressExists = !!basketData?.data?.commonShipToAddress
    //console.log("commonShipToAddressExists", commonShipToAddressExists)
    return commonShipToAddressExists
  }, [basketData?.data?.commonShipToAddress])

  function basketEmptyCheck() {
    return basketEmpty ? !hasLineItems : matchAll
  }

  function b2bCustomerCheck() {
    return isB2B ? storefrontContext?.isB2B === true : matchAll
  }

  function b2cCustomerCheck() {
    return isB2C ? storefrontContext?.isB2B === false : matchAll
  }

  function ociCheck() {
    return isOCI ? storefrontContext?.oci === true : matchAll
  }

  function notOciCheck() {
    return isNotOCI ? storefrontContext?.oci === false : matchAll
  }

  function anonymousCheck() {
    return isAnonymous ? storefrontContext?.isAnonymous === true : matchAll
  }

  function notAnonymousCheck() {
    return isNotAnonymous ? storefrontContext?.isAnonymous === false : matchAll
  }

  const shipToAddressSameAsInvoiceToAddress = useMemo(() => {
    const shipToAddress = basketData?.data?.commonShipToAddress
    const invoiceToAddress = basketData?.data?.invoiceToAddress

    if (shipToAddress && invoiceToAddress) {
      if (shipToAddress === invoiceToAddress) {
        return true
      }
    }
    return false
  }, [basketData])

  function basketNotEmptyCheck() {
    return basketNotEmpty ? hasLineItems : matchAll
  }

  function basketHasInvoiceToAddressCheck() {
    return basketHasInvoiceToAddress ? hasInvoiceToAddress : matchAll
  }

  function basketHasShipToAddressCheck() {
    return basketHasShipToAddress ? hasShipToAddress : matchAll
  }

  function basketShipToAddressSameAsInvoiceToAddressCheck() {
    return basketShipToAddressSameAsInvoiceToAddress ? shipToAddressSameAsInvoiceToAddress : matchAll
  }

  function hasAddressesCheck() {
    return hasAddresses ? addresses && addresses.length > 0 : matchAll
  }

  let showContentClient = matchAll
    ? showContent &&
      b2bCustomerCheck() &&
      b2cCustomerCheck() &&
      ociCheck() &&
      notOciCheck() &&
      anonymousCheck() &&
      notAnonymousCheck() &&
      basketEmptyCheck() &&
      basketNotEmptyCheck() &&
      basketHasInvoiceToAddressCheck() &&
      basketHasShipToAddressCheck() &&
      basketShipToAddressSameAsInvoiceToAddressCheck() &&
      hasAddressesCheck()
    : showContent ||
      b2bCustomerCheck() ||
      b2cCustomerCheck() ||
      ociCheck() ||
      notOciCheck() ||
      anonymousCheck() ||
      notAnonymousCheck() ||
      basketEmptyCheck() ||
      basketNotEmptyCheck() ||
      basketHasInvoiceToAddressCheck() ||
      basketHasShipToAddressCheck() ||
      basketShipToAddressSameAsInvoiceToAddressCheck() ||
      hasAddressesCheck()

  //console.log(blok.name + " showContentClient: " + showContent)

  return (
    <>
      {
        //If one of the basket tests is needed then wait for basket loader
        (basketNotEmpty ||
        basketEmpty ||
        basketHasShipToAddress ||
        basketHasInvoiceToAddress ||
        basketShipToAddressSameAsInvoiceToAddress
          ? basketData && basketLineItems
          : true) &&
        (negation ? !showContentClient : showContentClient) &&
        children ? (
          <span className={cn("", responsiveWidthVariants(blok.width))} {...storyblokEditable(blok)}>
            {children}
          </span>
        ) : (
          <></>
        )
      }
    </>
  )
}
